import { useState } from "react";
import {
	listClaimReservations,
	createClaimReservation,
	removeClaimReservation,
	recoverClaimReservation,
	ListReservationsParams
} from "../utils/api/reservations";
import {
	ListClaimReservationsResponse,
	ClaimReservation,
	CreateClaimReservationResponse,
	RemoveClaimReservationResponse,
	RecoverClaimReservationResponse,
	ClaimsStatus
} from "../utils/api/protos/messages/claims.message_pb";
import { PaginatedApiResponse, PaginationResponse } from "../utils/types";
import { dummyPaginatedApiResponse, RESERVATION_SUCCESS_MESSAGES, RESERVATION_ERROR_MESSAGES } from "../utils/consts";
import useClaimsToast from "./useToast";
import { convertUnixTimestampToLocalDate } from "../utils/helpers";

/**
 * Maps reservation data from proto to table format
 * NOTE: Backend ensures reservations are only created for Claims Analysts
 */
const mapReservationToTableData = (reservation: ClaimReservation) => {
	const user = reservation.getUser();
	const createdByUser = reservation.getCreatedByUser();
	const removedByUser = reservation.getRemovedByUser();
	const isDeleted = reservation.getIsDeleted();

	const reservationObj = reservation.toObject();
	const claimStatus = "claimStatus" in reservationObj ? reservationObj.claimStatus! : -1;

	const hasPassGroup = reservation.hasPassGroupId();
	const hasBenefit = reservation.hasBenefitCode();

	return {
		id: reservation.getId(),
		userId: reservation.getUserId(),
		analystName: user ? user.getName() : "Unknown",
		passGroupId: hasPassGroup ? reservation.getPassGroupId() : "",
		isAllPassGroups: hasPassGroup ? 0 : 1,
		state: reservation.hasLegalFilingState() ? reservation.getLegalFilingState() : "",
		benefitCode: hasBenefit ? reservation.getBenefitCode() : "",
		isAllBenefits: hasBenefit ? 0 : 1,
		claimStatus: claimStatus,
		status: reservation.getStatus(),
		// Store isDeleted as a number (0 or 1) to satisfy KVP type constraint
		isDeleted: isDeleted ? 1 : 0,
		createdByName: createdByUser ? createdByUser.getName() : "Unknown",
		createdOn: convertUnixTimestampToLocalDate(reservation.getCreatedOn()).toLocaleString(),
		removedByName: removedByUser ? removedByUser.getName() : "",
		removedOn: reservation.hasRemovedOn()
			? convertUnixTimestampToLocalDate(reservation.getRemovedOn()).toLocaleString()
			: ""
	};
};

/**
 * Maps API response to pagination data
 */
const mapPaginationData = (response: ListClaimReservationsResponse): PaginationResponse => {
	const pagination = response.getPaginationResponse();
	if (!pagination) {
		return {
			current_page: 1,
			first_page: 1,
			last_page: 1,
			perPage: 20,
			total: 0
		};
	}

	return {
		current_page: pagination.getCurrentPage(),
		first_page: pagination.getFirstPage(),
		last_page: pagination.getLastPage(),
		perPage: pagination.getLimit(),
		total: pagination.getTotal()
	};
};

/**
 * Custom hook for managing claim reservations
 */
export const useReservations = () => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [reservationsData, setReservationsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const toast = useClaimsToast();

	/**
	 * Fetches reservations list with filters and pagination
	 */
	const fetchReservations = async (params: ListReservationsParams): Promise<void> => {
		setIsLoading(true);

		try {
			const response: ListClaimReservationsResponse = await listClaimReservations(params);

			const returnedReservationsData = response.getReservationsList().map(mapReservationToTableData);
			const returnedPaginationData = mapPaginationData(response);

			setReservationsData({
				data: returnedReservationsData,
				meta: returnedPaginationData
			});
		} catch (err) {
			const errorMessage = (err as Error).message || RESERVATION_ERROR_MESSAGES.GENERAL_ERROR;
			toast("Error", errorMessage, "error");
		} finally {
			setIsLoading(false);
		}
	};

	/**
	 * Creates a new claim reservation
	 */
	const createReservation = async (
		userId: number,
		passGroupId?: string,
		benefitCode?: string,
		legalFilingState?: string,
		claimStatus?: ClaimsStatus
	): Promise<CreateClaimReservationResponse | null> => {
		setIsLoading(true);

		try {
			const response: CreateClaimReservationResponse = await createClaimReservation(
				userId,
				passGroupId,
				benefitCode,
				legalFilingState,
				claimStatus
			);

			toast("Success", RESERVATION_SUCCESS_MESSAGES.CREATED, "success");
			return response;
		} catch (err) {
			const errorMessage = (err as Error).message || RESERVATION_ERROR_MESSAGES.GENERAL_ERROR;

			// Check for duplicate/constraint errors
			if (
				errorMessage.includes("already exists") ||
				errorMessage.includes("unique constraint") ||
				errorMessage.includes("reservation with this pass group")
			) {
				toast("Error", errorMessage, "error");
			} else if (errorMessage.includes("Claims Analyst") || errorMessage.includes("not found")) {
				toast("Error", RESERVATION_ERROR_MESSAGES.NOT_ANALYST, "error");
			} else {
				toast("Error", errorMessage, "error");
			}
			return null;
		} finally {
			setIsLoading(false);
		}
	};

	/**
	 * Removes (soft deletes) a claim reservation
	 */
	const removeReservation = async (id: number): Promise<RemoveClaimReservationResponse | null> => {
		setIsLoading(true);

		try {
			const response: RemoveClaimReservationResponse = await removeClaimReservation(id);
			toast("Success", RESERVATION_SUCCESS_MESSAGES.REMOVED, "success");
			return response;
		} catch (err) {
			const errorMessage = (err as Error).message || RESERVATION_ERROR_MESSAGES.GENERAL_ERROR;
			toast("Error", errorMessage, "error");
			return null;
		} finally {
			setIsLoading(false);
		}
	};

	/**
	 * Recovers a soft-deleted claim reservation
	 */
	const recoverReservation = async (id: number): Promise<RecoverClaimReservationResponse | null> => {
		setIsLoading(true);

		try {
			const response: RecoverClaimReservationResponse = await recoverClaimReservation(id);
			toast("Success", RESERVATION_SUCCESS_MESSAGES.RECOVERED, "success");
			return response;
		} catch (err) {
			const errorMessage = (err as Error).message || RESERVATION_ERROR_MESSAGES.GENERAL_ERROR;
			toast("Error", errorMessage, "error");
			return null;
		} finally {
			setIsLoading(false);
		}
	};

	return {
		isLoading,
		reservationsData,
		fetchReservations,
		createReservation,
		removeReservation,
		recoverReservation,
		setIsLoading
	};
};
